import { useParams } from 'react-router-dom';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { DEFAULT_DATE_PATTERN, STOCK_CODE_SEARCH_RESULTS_TO_SHOW } from '@/config/common.config';
import { SearchProducts } from '@/graphql/contracts/_generated_/searchProducts.query';
import useGetContractDetails from '@/hooks/contract/useGetContractDetails.hook';
import { getBillingTypes } from '@/utils/data-helpers/billingTypes';
import { getBillingIntervals } from '@/utils/data-helpers/intervals';
import { getUnitPriceFriquencies } from '@/utils/data-helpers/unitPriceFriquencies';
import { NumberUtils } from '@/utils/numbers.utils';
import { getEmptyContractLineData } from './hooks/useCreateContractLine';
import { validator } from './validation/validator';
import { useModalWindowApi } from '@/components/ModalWindowApi/useModalWindowApi';
import CustomBillingInterval
  from '../ContractDetails/ContractLines/components/CustomBillingInterval/CustomBillingInterval';
import removeBtnStyles
  from '@/components/common/TableGrid/CustomComponents/AgGridRemoveButton/remove-button.module.scss';
import { VARIABLE_TYPE } from '@/config/statuses.config';
import useGetProducts from '../ContractDetails/ContractLines/hooks/useGetProducts';
import { BillingTypeEnum, ProductOutputType, PurchaseOrderOutputType } from '@/graphql/contracts/_generated_/contracts';
import { ContractLineCommonData } from '@/graphql/contracts/_generated_/ContractLineCommonData.fragment';
import { EnvUtils } from '@/utils/env.utils';
import { getCustomerLabel } from '../ContractDetails/ContractLines/grid/helper';
import useSearchCustomers from '../AddContract/hooks/useSearchCustomers';
import useGetPurchaseOrders from '../ContractDetails/ContractLines/hooks/useGetPurchaseOrders';

export function useColumns(): ColDef[] {
  const { contractId }: { contractId: UUID } = useParams();
  const { contract } = useGetContractDetails(contractId);
  const { addModal } = useModalWindowApi();
  const { getProducts } = useGetProducts();
  const searchShipToCustomer = useSearchCustomers();
  const searchInvoiceToCustomer = useSearchCustomers();
  const searchPurchaseOrders = useGetPurchaseOrders();

  const billingIntervals: Pick<BillingInterval, 'billingInterval' | 'formattedBillingInterval'>[] = [
    ...getBillingIntervals(),
    {
      billingInterval: 'add-new-interval',
      formattedBillingInterval: 'Add Interval',
    },
  ];
  const billingTypesForNonVariable = useMemo(
    () =>
      getBillingTypes().filter(
        type => type.formattedType !== VARIABLE_TYPE
      ),
    []
  );

  return [
    {
      colId: 'remove-button',
      cellRenderer: 'RemoveButton',
      minWidth: 45,
      maxWidth: 45,
      suppressMovable: true,
      suppressNavigable: true,
      cellClass: removeBtnStyles['remove-button-cell'],
      cellRendererParams: ({ api, data }: ICellRendererParams) => ({
        onClick: () => {
          api.applyTransaction({ remove: [data] });
          const rowCount = api.getDisplayedRowCount();

          if (rowCount === 0) {
            api.applyTransaction({
              add: [getEmptyContractLineData()],
              addIndex: 0
            });

            api.startEditingCell({
              rowIndex: 0,
              colKey: api.getAllDisplayedColumns()[1]
            });
          }
        }
      })
    },
    {
      colId: 'stockCode',
      headerName: 'Stock Code',
      field: 'stockCode',
      headerClass: 'ag-header-cell--mandatory',
      editable: true,
      initialWidth: 350,
      minWidth: 250,
      suppressMovable: true,
      cellEditor: 'ProductDropdownEditorAsync',
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: validator.getErrorsAt(colDef?.field!, data)
      }),
      valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
        const optionValue = value as SearchProducts['search'][number];

        if (optionValue?.stockCode && optionValue?.productName) {
          return `${optionValue.stockCode} - ${optionValue?.stockName}`;
        }

        return '';
      },
      cellEditorParams: () => ({
        handleInputChange: getProducts,
        billingTypes: [BillingTypeEnum.Subscription, BillingTypeEnum.Initials, BillingTypeEnum.Deposit],
        searchResults: STOCK_CODE_SEARCH_RESULTS_TO_SHOW,
        getOptionValue: (option: ProductOutputType) => option.stockCode,
        getOptionLabel: (option: ProductOutputType) => `${option.stockCode} - ${option.stockName}`,
        saveOnChange: true,
      }),
    },
    {
      colId: 'quantity',
      headerName: 'Quantity',
      field: 'quantity',
      headerClass: 'ag-header-cell--mandatory',
      cellStyle: () => ({ textAlign: 'right' }),
      editable: true,
      initialWidth: 110,
      minWidth: 110,
      suppressMovable: true,
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: validator.getErrorsAt(colDef?.field!, data)
      }),
      valueParser: ({ newValue }) => {
        const quantity = NumberUtils.parseIntegerInput(String(newValue));
        return isNaN(quantity) ? newValue : quantity;
      }
    },
    {
      colId: 'unitType',
      headerName: 'Unit Type',
      field: 'unitType',
      editable: true,
      initialWidth: 100,
      minWidth: 100,
      suppressMovable: true,
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: validator.getErrorsAt(colDef?.field!, data)
      }),
      valueSetter: ({ data, newValue }) => {
        data.unitType = newValue.trim() ? newValue : null;
        return true;
      }
    },
    {
      colId: 'unitPrice',
      headerName: 'Unit Price',
      headerClass: 'ag-header-cell--mandatory',
      field: 'unitPrice',
      cellStyle: () => ({ textAlign: 'right' }),
      editable: true,
      initialWidth: 130,
      minWidth: 130,
      suppressMovable: true,
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: validator.getErrorsAt(colDef?.field!, data)
      }),
      valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
        return NumberUtils.prettifyWithRound(value) ?? '';
      },
      valueParser: ({ newValue }) => {
        const unitPrice = NumberUtils.parseNumericInput(String(newValue));
        return isNaN(unitPrice) ? newValue : unitPrice;
      }
    },
    {
      colId: 'billingType',
      headerName: 'Billing Type',
      field: 'billingType',
      headerClass: 'ag-header-cell--mandatory',
      editable: true,
      initialWidth: 150,
      minWidth: 150,
      suppressMovable: true,
      cellEditor: 'DropdownEditor',
      cellRenderer: 'CellRenderer',
      cellEditorParams: () => ({
        options: billingTypesForNonVariable,
        getOptionValue: (option: BillingType): string => option.type,
        getOptionLabel: (option: BillingType): string => option.formattedType,
        saveOnChange: true,
      }),
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: validator.getErrorsAt(colDef?.field!, data)
      }),
      valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
        return (value as BillingType)?.formattedType || '';
      },
      valueSetter: ({ data, newValue }) => {
        data.billingType = newValue;
        return true;
      }
    },
    {
      field: 'unitPriceFrequency',
      colId: 'unitPriceFrequency',
      headerName: 'Unit Price Frequency',
      editable: true,
      initialWidth: 150,
      minWidth: 150,
      suppressMovable: true,
      cellEditor: 'DropdownEditor',
      cellRenderer: 'CellRenderer',
      cellEditorParams: () => ({
        options: getUnitPriceFriquencies(),
        getOptionValue: (option: UnitPriceFrequency) => option.frequency,
        getOptionLabel: (option: UnitPriceFrequency) => option.formattedFrequency,
        saveOnChange: true,
      }),
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: validator.getErrorsAt(colDef?.field!, data)
      }),
      valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
        return (value as UnitPriceFrequency)?.formattedFrequency || '';
      },
      valueSetter: ({ data, newValue }) => {
        data.unitPriceFrequency = newValue;
        return true;
      }
    },
    {
      colId: 'billingInterval',
      headerName: 'Billing Interval',
      field: 'billingInterval',
      editable: true,
      initialWidth: 150,
      minWidth: 150,
      suppressMovable: true,
      cellEditor: 'DropdownEditor',
      cellEditorParams: () => {
        return ({
          options: billingIntervals,
          getOptionValue: (option: BillingInterval): string => option.billingInterval,
          getOptionLabel: (option: BillingInterval): string => option.formattedBillingInterval,
          saveOnChange: true,
          handleChangeOption: (
            selectedOption: BillingInterval,
            applyChanges: (selectedOption: BillingInterval) => void,
            cancelChanges: () => void
          ) => {
            if (selectedOption.billingInterval === 'add-new-interval') {
              addModal({
                modalType: 'default',
                title: 'Add interval manually',
                onCloseCallback: cancelChanges,
                ModalContent: () => (
                  <CustomBillingInterval
                    applyChanges={applyChanges}
                    withConfirmation={false}
                  />
                )
              });
            }
            else {
              applyChanges(selectedOption);
            }
          }
        });
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: validator.getErrorsAt(colDef?.field!, data)
      }),
      valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
        return (value as BillingInterval)?.formattedBillingInterval || '';
      }
    },
    {
      colId: 'billingStartDate',
      headerName: 'Last Billing Start Date',
      field: 'billingStartDate',
      headerClass: 'ag-header-cell--mandatory',
      editable: true,
      initialWidth: 200,
      minWidth: 180,
      suppressMovable: true,
      cellEditor: 'DatepickerEditor',
      cellRenderer: 'CellRenderer',
      cellEditorParams: () => ({
        keepFocusOnCalendarClosed: true,
        saveOnChange: true,
      }),
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: validator.getErrorsAt(colDef?.field!, data, { contract })
      }),
      valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
        if (value && !Number.isNaN(Date.parse(value))) {
          return `${dayjs(value).format(DEFAULT_DATE_PATTERN)}`;
        }
        return '';
      },
    },
    ...(!EnvUtils.isProRataFirstPeriodEnabled() ? [{
      colId: 'lineStartDate',
      headerName: 'Line Start Date',
      field: 'lineStartDate',
      headerClass: 'ag-header-cell--mandatory',
      editable: true,
      initialWidth: 200,
      minWidth: 180,
      suppressMovable: true,
      cellEditor: 'DatepickerEditor',
      cellRenderer: 'CellRenderer',
      cellEditorParams: () => ({
        keepFocusOnCalendarClosed: true,
        saveOnChange: true,
      }),
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: validator.getErrorsAt(colDef?.field!, data, { contract })
      }),
      valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
        if (value && !Number.isNaN(Date.parse(value))) {
          return `${dayjs(value).format(DEFAULT_DATE_PATTERN)}`;
        }
        return '';
      }
    }] : []),
    ...(EnvUtils.isProRataFirstPeriodEnabled() ? [{
      colId: 'proRataEndDate',
      headerName: 'Pro Rata End Date',
      field: 'proRataEndDate',
      editable: true,
      initialWidth: 200,
      minWidth: 180,
      suppressMovable: true,
      cellEditor: 'DatepickerEditor',
      cellRenderer: 'CellRenderer',
      cellEditorParams: () => ({
        keepFocusOnCalendarClosed: true,
        saveOnChange: true,
      }),
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: validator.getErrorsAt(colDef?.field!, data, { contract })
      }),
      valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
        if (value && !Number.isNaN(Date.parse(value))) {
          return `${dayjs(value).format(DEFAULT_DATE_PATTERN)}`;
        }
        return '';
      }
    }] : []),
    {
      colId: 'comments',
      headerName: 'Comments',
      field: 'comments',
      editable: true,
      initialWidth: 250,
      minWidth: 150,
      suppressMovable: true,
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: validator.getErrorsAt(colDef?.field!, data)
      }),
      valueSetter: ({ data, newValue }) => {
        data.comments = newValue.trim() ? newValue : null;
        return true;
      }
    },
    ...([
      {
        colId: 'shipToCustomer',
        headerName: 'Ship To Account',
        field: 'shipToCustomer',
        initialWidth: 350,
        minWidth: 250,
        editable: true,
        cellEditor: 'CustomerDropdownEditorAsync',
        cellEditorParams: ({ data }: ICellRendererParams) => ({
          handleInputChange: searchShipToCustomer.searchCustomers,
          saveOnChange: true,
          getOptionValue: (
            option:
            | ContractLineCommonData['shipToCustomer']
            | undefined
            | null
          ) => option,
          getOptionLabel: (
            option:
            | ContractLineCommonData['shipToCustomer']
            | undefined
            | null
          ) => getCustomerLabel(option),
          placeholder: getCustomerLabel(data?.shipToCustomer ?? contract?.shipToCustomer),
        }),
        valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
          const option = value as
            | ContractLineCommonData['shipToCustomer']
            | undefined
            | null;
          return (
            <span className="c-ship-to">
              {getCustomerLabel(option)}
            </span>
          );
        },
        cellRenderer: 'CellRenderer',
        cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
          errors: validator.getErrorsAt(colDef?.field!, data)
        }),
      },
      {
        colId: 'invoiceToCustomer',
        headerName: 'Invoice To Account',
        field: 'invoiceToCustomer',
        initialWidth: 350,
        minWidth: 250,
        editable: true,
        cellEditor: 'CustomerDropdownEditorAsync',
        cellEditorParams: ({ data }: ICellRendererParams) => ({
          handleInputChange: searchInvoiceToCustomer.searchCustomers,
          saveOnChange: true,
          getOptionValue: (
            option:
            | ContractLineCommonData['invoiceToCustomer']
            | undefined
            | null
          ) => option,
          getOptionLabel: (
            option:
            | ContractLineCommonData['invoiceToCustomer']
            | undefined
            | null
          ) => getCustomerLabel(option),
          placeholder: getCustomerLabel(data?.invoiceToCustomer ?? contract?.invoiceToCustomer),
        }),
        valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
          const option = value as
            | ContractLineCommonData['invoiceToCustomer']
            | undefined
            | null;
          return (
            <span className="c-invoice-to">
              {getCustomerLabel(option)}
            </span>
          );
        },
        cellRenderer: 'CellRenderer',
        cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
          errors: validator.getErrorsAt(colDef?.field!, data)
        }),
        valueSetter: ({ data, newValue }) => {
          data.invoiceToCustomer = newValue?.customerAccountNumber
            ? newValue
            : null;
          data.poNumber = null;
          return true;
        }
      },
      {
        colId: 'poNumber',
        headerName: 'PO Number',
        field: 'poNumber',
        initialWidth: 200,
        minWidth: 200,
        editable: true,
        cellEditor: 'PurchaseOrderDropdownEditorAsync',
        cellEditorParams: ({ data }: ICellRendererParams) => ({
          handleInputChange: searchPurchaseOrders.getPurchaseOrdersItems,
          customerId: data.invoiceToCustomer?.customerID ?? contract?.invoiceToCustomer?.customerID,
          customerCode:
            data.invoiceToCustomer?.customerAccountNumber ??
            contract?.invoiceToCustomer.customerAccountNumber,
          customerName: data.invoiceToCustomer?.customerName ?? contract?.invoiceToCustomer.customerName,
          currency: contract?.currency.currency,
          saveOnChange: true,
          getOptionValue: (
            option:
            | PurchaseOrderOutputType
            | undefined
            | null
          ) => option,
          getOptionLabel: (
            option:
            | PurchaseOrderOutputType
            | undefined
            | null
          ) => {
            return option?.poNumber || '';
          },
          placeholder: data?.poNumber
        }),
        valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
          const option = value as
            | ContractLineCommonData['poNumber']
            | undefined
            | null;
          return option || '';
        },
        cellRenderer: 'CellRenderer',
        cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
          errors: validator.getErrorsAt(colDef?.field!, data)
        }),
        valueSetter: ({ data, newValue }) => {
          data.poNumber = newValue?.poNumber;
          return true;
        }
      },
    ] as ColDef<ContractLine>[]),
  ];
}
